import { AppDispatch } from '@/stores'
import { useContext } from 'react'
import { useDispatch } from 'react-redux'
import { usePostComponentsTbGroupMutation } from '@/api/serverApi'
import { addComponent } from '@/stores/active-design'
import { EclipseIcon, LoadingSpinner } from '@cfs-fixings/shared'
import { createContext, useState } from 'react'
import { LoadsContext } from '../hooks/LoadsContext'

type LoadingContextType = {
  isLoading: boolean
  setLoading: (loading: boolean) => void
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined)

const TBoltGroupLoads = () => {
  const [isLoading, setLoading] = useState(false)

  return (
    <div className="w-full">
      <p className="text-xs pb-3 text-gray-550">
        Add and configure T-bolts to modify tension and shear loads.
      </p>
      <div className="w-full flex justify-center">
        <div className="w-full grid grid-cols-2 gap-3 max-w-[184px]">
          <LoadingContext.Provider value={{ isLoading, setLoading }}>
            {
              [1, 2, 3, 4].map((value, index) => (
                <TBoltGroupCreator
                  key={`tbolt-group-button-${index}-${value}`}
                  value={value}
                />
              ))
            }
          </LoadingContext.Provider>
        </div>
      </div>
    </div>
  )
}

type TBoltGroupCreateProps = {
  value: number
}

const TBoltGroupCreator = ({ value }: TBoltGroupCreateProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { design } = useContext(LoadsContext)
  const loading = useContext(LoadingContext)
  const [createTBoltGroup, { isLoading: isMutationLoading }] = usePostComponentsTbGroupMutation()

  const handleButtonOnClick = (value: number) => {
    loading?.setLoading(true)
    const castInAnchorChannel = design?.components
      .find(c => Object.prototype.hasOwnProperty.call(c, 'cast_in_anchor_channel'))

    if (!design || !castInAnchorChannel) {
      return null
    }

    const payload = {
      design_id: design?.id,
      parent_id: castInAnchorChannel?.id,
      count: value,
    }

    createTBoltGroup({
      body: payload,
    })
      .unwrap()
      .then((components) => {
        components.data.forEach((component) => {
          const tBolt = component.t_bolt
          dispatch(
            addComponent({
              id: component.id,
              group_id: component.group_id ?? '',
              model: component.model,
              parent_id: component.parent_id,
              type: component.type,
              product_variant_id: component.product_variant_id,
              position: {
                x: component.position?.x ?? 0,
                y: component.position?.y ?? 0,
                z: component.position?.z ?? 0,
              },
              t_bolt: {
                shear_force: tBolt ? tBolt.shear_force : '0',
                tension_force: tBolt ? tBolt.tension_force : '0',
                stand_off_distance: tBolt ? tBolt.stand_off_distance : '0',
                diameter: tBolt ? tBolt?.diameter : '0',
                length: tBolt ? tBolt.length : 0,
              },
            }),
          )
          loading?.setLoading(false)
        })
      })
      .catch(() => loading?.setLoading(false))
  }

  return (
    <button
      className="bg-gray-300 py-2.5 rounded border border-gray-400 text-gray-550 max-w-[86px] outline-black flex justify-center gap-x-2"
      onClick={() => handleButtonOnClick(value)}
      type="button"
      disabled={isMutationLoading || loading?.isLoading}
    >
      {
        isMutationLoading
        && (
          <div className="text-gray-550">
            <LoadingSpinner className="size-2" />
          </div>
        )
      }
      {
        !isMutationLoading
        && (
          Array
            .from({ length: value })
            .map((index) => (
              <EclipseIcon
                key={`tbolt-counts-${index}`}
                className="size-2"
              />
            ))
        )
      }
    </button>
  )
}

export default TBoltGroupLoads
